import React, { useRef, useState, useEffect, useCallback } from 'react';
import './DragCamera.css';

/**
 * Controle de câmera por drag do mouse
 * Clique e arraste para olhar ao redor
 */
const DragCamera = ({ onRotate, isLocked }) => {
  const [isDragging, setIsDragging] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const rotation = useRef({ x: 0, y: 0 });

  // Sensibilidade
  const SENSITIVITY = 0.003;

  // Iniciar drag
  const handleMouseDown = useCallback((e) => {
    if (!isLocked) return;
    setIsDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, [isLocked]);

  // Mover
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !isLocked) return;

    const deltaX = e.clientX - lastPos.current.x;
    const deltaY = e.clientY - lastPos.current.y;

    // Atualizar rotação
    rotation.current.y -= deltaX * SENSITIVITY; // Horizontal
    rotation.current.x -= deltaY * SENSITIVITY; // Vertical

    // Limitar rotação vertical (não virar de cabeça para baixo)
    rotation.current.x = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, rotation.current.x));

    // Callback
    onRotate(rotation.current);

    lastPos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging, isLocked, onRotate]);

  // Finalizar drag
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Event listeners
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Touch support
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (!isLocked || e.touches.length === 0) return;
      setIsDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchMove = (e) => {
      if (!isDragging || !isLocked || e.touches.length === 0) return;

      const deltaX = e.touches[0].clientX - lastPos.current.x;
      const deltaY = e.touches[0].clientY - lastPos.current.y;

      rotation.current.y -= deltaX * SENSITIVITY;
      rotation.current.x -= deltaY * SENSITIVITY;
      rotation.current.x = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, rotation.current.x));

      onRotate(rotation.current);

      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isLocked, onRotate]);

  return (
    <div 
      className={`drag-camera-overlay ${isDragging ? 'dragging' : ''} ${isLocked ? 'active' : ''}`}
      onMouseDown={handleMouseDown}
    >
      {/* Indicador visual quando ativo */}
      {isLocked && !isDragging && (
        <div className="drag-hint">
          Clique e arraste para olhar
        </div>
      )}
    </div>
  );
};

export default DragCamera;
